import { Typography } from 'components/atoms';
import { NewBreadCrumb } from 'components/molecules';
import { ViewScreen } from 'components/template';
import { endOfMonth, startOfMonth } from 'date-fns';
import jwt from 'jwt-decode';
import React, { useEffect, useMemo, useState } from 'react';
import { RootState, useAppSelector } from 'store';
import { agent } from 'services/service';
import { AttendanceLastImport } from 'services/AttendanceTrackingService';
import { formatAttendanceLastImportDate } from 'utils/formatAttendanceLastImport';
import {
  getLastMonthPeriod,
  type AttendanceDashboardPeriod
} from 'utils/attendanceDashboardPeriod';
import {
  AttendanceCalendarCore,
  getDefaultActiveMonth
} from './AttendanceCalendarCore';
import type { CalendarViewMode } from './calendarUtils';
import { useMyAttendanceCalendar } from './useMyAttendanceCalendar';

export const MyAttendanceCalendar = () => {
  const token: { name?: string } | '' = useAppSelector((state: RootState) =>
    state.auth?.user?.authData?.token
      ? jwt(state.auth.user.authData.token)
      : ''
  );

  const tokenDisplayName = useMemo(() => {
    if (typeof token === 'object' && token?.name) return token.name;
    return '';
  }, [token]);

  const [viewMode, setViewMode] = useState<CalendarViewMode>('monthly');
  const [period, setPeriod] = useState<AttendanceDashboardPeriod>(() =>
    getLastMonthPeriod()
  );
  const [activeMonth, setActiveMonth] = useState(() =>
    getDefaultActiveMonth(new Date())
  );

  const calendarRange = useMemo(() => {
    if (viewMode === 'monthly') {
      return {
        from: startOfMonth(activeMonth),
        to: endOfMonth(activeMonth)
      };
    }

    return {
      from: period.from,
      to: period.to
    };
  }, [viewMode, activeMonth, period.from, period.to]);

  const {
    epfNumber,
    recordsByDate,
    employeeName,
    minimumOfficeDays,
    legendItems,
    statusLabels,
    loading,
    error
  } = useMyAttendanceCalendar(calendarRange.from, calendarRange.to);

  const displayName = employeeName || tokenDisplayName || 'My Attendance';

  const [lastImport, setLastImport] = useState<AttendanceLastImport | null>(
    null
  );

  useEffect(() => {
    agent.AttendanceTrackingService.getLastUpdate()
      .then((res) => setLastImport(res.data))
      .catch(() => setLastImport(null));
  }, []);

  const lastUpdatedLabel = lastImport?.lastImportAt
    ? formatAttendanceLastImportDate(lastImport)
    : null;

  const handleViewModeChange = (mode: CalendarViewMode) => {
    setViewMode(mode);
    if (mode === 'monthly') {
      setActiveMonth((current) => startOfMonth(current));
    }
  };

  const handlePeriodChange = (next: AttendanceDashboardPeriod) => {
    setPeriod(next);
    setActiveMonth(startOfMonth(next.from));
  };

  const headerItem1 = (
    <>
      <Typography type="heading5" text="Aventude Pvt Ltd" />
      <NewBreadCrumb />
    </>
  );

  return (
    <ViewScreen
      portalTitle="Employee Portal"
      headerItem1={headerItem1}
      mainItem1={
        <AttendanceCalendarCore
          variant="employee"
          epfNumber={epfNumber}
          employeeName={displayName}
          recordsByDate={recordsByDate}
          legendItems={legendItems}
          statusLabels={statusLabels}
          loading={loading}
          error={error}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          rangeStart={calendarRange.from}
          rangeEnd={calendarRange.to}
          period={period}
          onPeriodChange={handlePeriodChange}
          activeMonth={activeMonth}
          onActiveMonthChange={setActiveMonth}
          minimumOfficeDays={minimumOfficeDays}
          lastUpdatedLabel={lastUpdatedLabel}
        />
      }
    />
  );
};
